<template>
  <div class="app-upload-field">
    <label v-if="label" class="label">{{ label }}</label>
    <div class="app-upload-panel" :class="{ 'app-upload-disabled': uploadDesabilitado }">
      <div class="app-upload-wrapper">
        <b-upload
          multiple
          drag-drop
          class="app-upload"
          v-model="pickerFiles"
          :disabled="uploadDesabilitado"
          @input="adicionarArquivos"
        >
          <div class="app-upload-content">
            <b-icon class="app-upload-icon" icon="upload" size="is-medium" />
            <p>Clique ou arraste arquivos nesta área</p>
            <p class="app-upload-hint">
              Até {{ quantidadeMaximaArquivos }} arquivos, {{ limiteMb }} MB cada
            </p>
          </div>
        </b-upload>

        <ul v-if="arquivos.length || anexosSalvos.length" class="app-upload-list">
          <li v-for="anexo in anexosSalvos" :key="`salvo-${anexo.id}`" class="app-upload-item">
            <span class="app-upload-filename" :title="anexo.nome">{{ anexo.nome }}</span>
            <button
              type="button"
              class="delete is-small"
              aria-label="Remover arquivo"
              :disabled="desabilitado"
              @click.prevent="removerAnexoSalvo(anexo.id)"
            />
          </li>
          <li
            v-for="(arquivo, index) in arquivos"
            :key="`novo-${arquivo.name}-${arquivo.size}-${index}`"
            class="app-upload-item"
          >
            <span class="app-upload-filename" :title="arquivo.name">{{ arquivo.name }}</span>
            <button
              type="button"
              class="delete is-small"
              aria-label="Remover arquivo"
              :disabled="desabilitado"
              @click.prevent="removerArquivo(index)"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getPncpDocumentUploadSizeErrorMessage,
  isWithinPncpDocumentUploadLimit,
  PNCP_DOCUMENT_UPLOAD_MAX_MB,
} from '@/utils/pncpDocumentUpload';

export default {
  name: 'AppUploadPanel',
  props: {
    label: {
      type: String,
      default: 'Documentos',
    },
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    anexosSalvos: {
      type: Array,
      default() {
        return [];
      },
    },
    quantidadeMaximaArquivos: {
      type: Number,
      default: 5,
    },
    desabilitado: {
      type: Boolean,
      default: false,
    },
    permitirSelecao: {
      type: Boolean,
      default: true,
    },
    limparAoDesabilitar: {
      type: Boolean,
      default: true,
    },
    mensagemBloqueado: {
      type: String,
      default: 'Upload indisponível no momento',
    },
    mensagemLimiteArquivos: {
      type: String,
      default: '',
    },
    mensagemTamanho: {
      type: String,
      default: getPncpDocumentUploadSizeErrorMessage(),
    },
    mensagemArquivoDuplicado: {
      type: String,
      default: 'Este arquivo já foi adicionado',
    },
  },
  data() {
    return {
      pickerFiles: [],
      limiteMb: PNCP_DOCUMENT_UPLOAD_MAX_MB,
    };
  },
  computed: {
    arquivos() {
      return this.value || [];
    },
    uploadDesabilitado() {
      return this.desabilitado || !this.permitirSelecao;
    },
    mensagemLimiteEfetiva() {
      return this.mensagemLimiteArquivos || `Máximo de ${this.quantidadeMaximaArquivos} arquivos`;
    },
    totalArquivos() {
      return this.anexosSalvos.length + this.arquivos.length;
    },
  },
  watch: {
    desabilitado(desabilitado) {
      if (desabilitado && this.limparAoDesabilitar && this.arquivos.length) {
        this.$emit('input', []);
      }
    },
    permitirSelecao(permitir) {
      if (!permitir && this.limparAoDesabilitar && this.arquivos.length) {
        this.$emit('input', []);
      }
    },
  },
  methods: {
    arquivoDuplicado(arquivo, lista) {
      return lista.some(item => item.name === arquivo.name && item.size === arquivo.size);
    },
    nomeJaUtilizado(nome) {
      const nomeNormalizado = (nome || '').trim().toLowerCase();
      return this.anexosSalvos.some(
        anexo => (anexo.nome || '').trim().toLowerCase() === nomeNormalizado,
      );
    },
    adicionarArquivos(files) {
      this.pickerFiles = [];

      if (!files) return;

      const novosArquivos = Array.isArray(files) ? files : [files];
      if (!novosArquivos.length) return;

      if (!this.permitirSelecao) {
        this.$alerta(this.mensagemBloqueado, 'is-danger');
        return;
      }

      if (this.desabilitado) return;

      const arquivoGrande = novosArquivos.find(
        arquivo => !isWithinPncpDocumentUploadLimit(arquivo.size),
      );
      if (arquivoGrande) {
        this.$alerta(this.mensagemTamanho, 'is-danger');
        return;
      }

      const referencia = [...this.arquivos];
      const candidatos = [];
      let duplicadoEncontrado = false;

      novosArquivos.forEach(arquivo => {
        if (this.arquivoDuplicado(arquivo, referencia) || this.nomeJaUtilizado(arquivo.name)) {
          duplicadoEncontrado = true;
          return;
        }

        candidatos.push(arquivo);
        referencia.push(arquivo);
      });

      if (!candidatos.length) {
        if (duplicadoEncontrado) {
          this.$alerta(this.mensagemArquivoDuplicado, 'is-danger');
        }
        return;
      }

      if (this.totalArquivos + candidatos.length > this.quantidadeMaximaArquivos) {
        this.$alerta(this.mensagemLimiteEfetiva, 'is-danger');
        return;
      }

      if (duplicadoEncontrado) {
        this.$alerta(this.mensagemArquivoDuplicado, 'is-warning');
      }

      this.$emit('input', [...this.arquivos, ...candidatos]);
    },
    removerArquivo(index) {
      if (this.desabilitado) return;

      const arquivos = this.arquivos.slice();
      arquivos.splice(index, 1);
      this.$emit('input', arquivos);
      this.$emit('remover', index);
    },
    removerAnexoSalvo(anexoId) {
      if (this.desabilitado) return;

      const anexosSalvos = this.anexosSalvos.filter(anexo => Number(anexo.id) !== Number(anexoId));
      this.$emit('update:anexosSalvos', anexosSalvos);
    },
  },
};
</script>

<style src="@/styles/upload.scss" lang="scss" scoped />
